// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Every shader in the tool. GLSL ES 3.00, WebGL2.
 *
 * Three things here are deliberate and would each be wrong if written the
 * obvious way.
 *
 * 1. THERE IS NO PROJECTION MATRIX. Off-axis parallax is exactly affine in
 *    inverse depth (see core/camera.ts), and the network hands us inverse
 *    depth, so the whole camera is two numbers: a screen-space shift and a
 *    zoom. `gl_Position.w` stays at 1, which also makes texture interpolation
 *    affine across each triangle — exactly right for a displaced sheet.
 *
 * 2. THE DEPTH TEXTURE IS SAMPLED BY HAND. WebGL2 promotes OES_texture_float to
 *    core but explicitly does NOT promote OES_texture_float_linear, so LINEAR
 *    filtering of an R32F texture is not available without an extension. The
 *    texture is therefore NEAREST and `sampleDepth()` does the bilinear tap
 *    with four texelFetches, which is exact and costs nothing measurable.
 *
 * 3. THE HOLE MAP USES THE SMALLEST SINGULAR VALUE of the UV Jacobian, not
 *    sqrt(|det J|). Disocclusion smear is a ONE-DIMENSIONAL stretch: a pure 4x
 *    smear along one axis has determinant 4, so the determinant form reports it
 *    as 2x and under-flags the artefact by a square root. The singular value
 *    measures magnification along the most-stretched direction, which is the
 *    thing the eye actually sees.
 */

/** Shared helpers, textually prefixed to the shaders that need them. */
const DEPTH_SAMPLER = /* glsl */ `
uniform highp sampler2D uDepth;
uniform vec2 uDepthSize;

// Bilinear sample of a NEAREST-filtered R32F texture. See note 2 above.
float sampleDepth(vec2 uv) {
  vec2 t = uv * uDepthSize - 0.5;
  vec2 base = floor(t);
  vec2 f = t - base;
  ivec2 i0 = ivec2(base);
  ivec2 hi = ivec2(uDepthSize) - 1;
  ivec2 a = clamp(i0, ivec2(0), hi);
  ivec2 b = clamp(i0 + ivec2(1, 0), ivec2(0), hi);
  ivec2 c = clamp(i0 + ivec2(0, 1), ivec2(0), hi);
  ivec2 d = clamp(i0 + ivec2(1, 1), ivec2(0), hi);
  float va = texelFetch(uDepth, a, 0).r;
  float vb = texelFetch(uDepth, b, 0).r;
  float vc = texelFetch(uDepth, c, 0).r;
  float vd = texelFetch(uDepth, d, 0).r;
  return mix(mix(va, vb, f.x), mix(vc, vd, f.x), f.y);
}
`;

/**
 * The displaced sheet.
 *
 * No position attribute exists: a vertex's grid coordinate is derived from
 * gl_VertexID, so the only buffer bound is the index buffer. That halves the
 * upload and means changing the grid resolution costs nothing but a uniform.
 */
export const PARALLAX_VERT = /* glsl */ `#version 300 es
precision highp float;
${DEPTH_SAMPLER}

uniform int uGrid;          // cells per side
uniform vec2 uUvScale;      // cover-crop of the source into the output frame
uniform vec2 uUvOffset;
uniform vec2 uShift;        // NDC displacement per unit of (depth - pivot)
uniform float uPivot;       // depth of the convergence plane
uniform float uZoom;        // scale about the frame centre
uniform float uDepthZoom;   // extra scale per unit of (depth - pivot): a dolly

out vec2 vUv;
out float vDepth;

void main() {
  int n = uGrid + 1;
  int ix = gl_VertexID % n;
  int iy = gl_VertexID / n;
  vec2 g = vec2(float(ix), float(iy)) / float(uGrid);

  // Source coordinate this grid point reads from.
  vec2 uv = g * uUvScale + uUvOffset;
  float d = sampleDepth(uv);

  // The sheet in normalised device coordinates, before the camera.
  //
  // Two terms, and they are different movements. uShift is a lateral move:
  // everything slides, near things further. uDepthZoom is a move along the view
  // axis: everything is magnified, near things more — which is what a dolly
  // actually does, and which a uniform zoom cannot express at all.
  vec2 ndc = g * 2.0 - 1.0;
  ndc *= uZoom + uDepthZoom * (d - uPivot);
  ndc += uShift * (d - uPivot);

  // Near surfaces must win where the sheet folds over itself, so depth goes
  // into gl_Position.z rather than being resolved by draw order. Larger depth
  // is closer (inverse depth), and smaller z is nearer in NDC.
  gl_Position = vec4(ndc, 1.0 - 2.0 * d, 1.0);
  vUv = uv;
  vDepth = d;
}
`;

export const PARALLAX_FRAG = /* glsl */ `#version 300 es
precision highp float;

uniform sampler2D uPhoto;
uniform vec2 uPhotoSize;
uniform float uBaseMag;     // legitimate magnification, screen px per texel
uniform float uHoleMap;     // 0 = off, 1 = overlay the invented regions
uniform float uHoleGate;    // how much stretch counts as invention

in vec2 vUv;
in float vDepth;
out vec4 outColour;

/*
 * Smallest singular value of the 2x2 Jacobian of the source coordinate with
 * respect to screen position, in texels per pixel. See note 3 in the header.
 */
float minSingular(mat2 j) {
  float a = j[0][0], c = j[0][1];
  float b = j[1][0], d = j[1][1];
  float e = (a * a + c * c + b * b + d * d) * 0.5;
  float f = (a * a + c * c - b * b - d * d) * 0.5;
  float g = a * b + c * d;
  float r = sqrt(max(f * f + g * g, 0.0));
  return sqrt(max(e - r, 0.0));
}

void main() {
  vec3 rgb = texture(uPhoto, vUv).rgb;

  if (uHoleMap > 0.5) {
    mat2 j = mat2(dFdx(vUv) * uPhotoSize, dFdy(vUv) * uPhotoSize);
    float s = minSingular(j);
    float mag = s > 1e-8 ? 1.0 / s : 1e8;
    float ratio = mag / max(uBaseMag, 1e-6);
    float flag = smoothstep(uHoleGate, uHoleGate * 1.6, ratio);
    // Desaturate what is real, paint what is invented. The point is that the
    // person can see exactly which pixels the tool made up.
    float luma = dot(rgb, vec3(0.2126, 0.7152, 0.0722));
    rgb = mix(vec3(luma * 0.55), vec3(1.0, 0.28, 0.16), flag);
  }

  outColour = vec4(rgb, 1.0);
}
`;

/** A full-screen triangle, for every single-pass effect below. */
export const FULLSCREEN_VERT = /* glsl */ `#version 300 es
precision highp float;
out vec2 vUv;
void main() {
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

/**
 * Depth of field.
 *
 * A golden-angle (Vogel) disk gather, single pass, with a reach test on every
 * tap. A separable Gaussian is the obvious implementation and it is wrong: with
 * a per-pixel radius the two passes disagree about how far to reach and the
 * result is cross-shaped streaking that reads as a rendering bug. The reach
 * test — "this tap only contributes if its own circle of confusion actually
 * reaches me" — is what stops a blurred background bleeding over a sharp
 * foreground edge, because a sharp pixel has a radius near zero and cannot
 * reach anything.
 *
 * Everything is done in linear light. The photo texture is SRGB8_ALPHA8, so the
 * sampler linearises on read and the framebuffer converts back on write; a blur
 * computed in gamma space is the single most visible bokeh regression there is.
 */
export const DOF_FRAG = /* glsl */ `#version 300 es
precision highp float;
${DEPTH_SAMPLER}

uniform sampler2D uPhoto;
uniform vec2 uUvScale;
uniform vec2 uUvOffset;
uniform vec2 uPixel;        // one output pixel, in uv units
uniform float uFocus;       // depth of the focal plane
uniform float uStrength;    // max circle of confusion, in output pixels

in vec2 vUv;
out vec4 outColour;

const int TAPS = 56;
const float GOLDEN = 2.39996323;

float cocAt(vec2 uv) {
  return (sampleDepth(uv) - uFocus) * uStrength;
}

void main() {
  vec2 uv = vUv * uUvScale + uUvOffset;
  float coc = cocAt(uv);
  float radius = abs(coc);

  vec3 sum = texture(uPhoto, uv).rgb;
  float weight = 1.0;

  for (int i = 0; i < TAPS; i++) {
    float fi = float(i) + 0.5;
    float r = radius * sqrt(fi / float(TAPS));
    float a = fi * GOLDEN;
    vec2 offset = vec2(cos(a), sin(a)) * r;
    vec2 tapUv = uv + offset * uPixel * uUvScale;
    float tapCoc = abs(cocAt(tapUv));
    float dist = length(offset);
    // The whole test: does this tap's own circle of confusion reach the centre
    // pixel? A sharp pixel has a radius near zero and so cannot spread onto
    // anything, which is what keeps a blurred background off a sharp edge.
    float w = smoothstep(dist - 1.0, dist + 1.0, tapCoc);
    sum += texture(uPhoto, tapUv).rgb * w;
    weight += w;
  }
  outColour = vec4(sum / max(weight, 1e-4), 1.0);
}
`;

/**
 * Dubois anaglyph.
 *
 * A naive channel split (red from the left eye, green and blue from the right)
 * throws away the cross-channel information and produces severe retinal rivalry
 * on saturated reds. Dubois solves a least-squares projection onto what the
 * glasses actually pass instead.
 *
 * The coefficients here are the modern LCD-primaries/5450C-glasses solution.
 * They are NOT the ones from the 2001 ICASSP paper — that paper publishes the
 * inputs and the recipe but never prints the matrix, and the values that
 * circulate as "the Dubois matrix" were solved for a Sony Trinitron CRT and
 * Roscolux gel filters. The self-test is the row sums: white in both eyes must
 * come out white, and these sum to 1.0000, 1.0001 and 0.9999.
 *
 * Applied in linear light, per section 2.1 of the paper (the gamma is undone
 * before the matrices act and reapplied after clipping) — which the SRGB8
 * texture format and an SRGB draw buffer do for us.
 */
export const ANAGLYPH_FRAG = /* glsl */ `#version 300 es
precision highp float;

uniform sampler2D uLeft;
uniform sampler2D uRight;

in vec2 vUv;
out vec4 outColour;

const mat3 M_LEFT = mat3(
   0.4154, -0.0458, -0.0547,
   0.4710, -0.0484, -0.0615,
   0.1669, -0.0257,  0.0128
);
const mat3 M_RIGHT = mat3(
  -0.0109,  0.3756, -0.0651,
  -0.0364,  0.7333, -0.1287,
  -0.0060,  0.0111,  1.2971
);

void main() {
  vec3 l = texture(uLeft, vUv).rgb;
  vec3 r = texture(uRight, vUv).rgb;
  outColour = vec4(clamp(M_LEFT * l + M_RIGHT * r, 0.0, 1.0), 1.0);
}
`;

/** Straight copy, used to resolve an SRGB render target for readback. */
export const COPY_FRAG = /* glsl */ `#version 300 es
precision highp float;
uniform sampler2D uSource;
in vec2 vUv;
out vec4 outColour;
void main() { outColour = vec4(texture(uSource, vUv).rgb, 1.0); }
`;
